import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Page, Layout, Card, Text, Box, Badge, Button, Spinner,
  InlineStack, Toast, Link, Icon
} from '@shopify/polaris';
import {
  PlusIcon, CheckCircleIcon, ExternalIcon
} from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../auth';
import { useI18n } from '../i18n';
import { formatMoney } from '../constants';

// Sparkline drawn from the real daily order counts returned by
// GET /api/stats (oldest → newest). A flat line means the store
// genuinely had no plan orders in the window.
function MiniAreaChart({ counts }) {
  const data = counts && counts.length > 0 ? counts : [0];
  const w = 300, h = 60, pad = 6;
  const max = Math.max(...data, 1);
  const step = data.length > 1 ? w / (data.length - 1) : 0;
  const points = data.map((v, i) => [
    i * step,
    h - pad - (v / max) * (h - pad * 2)
  ]);
  const line = points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(' ');
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      style={{ width: '100%', height: 56, display: 'block', marginTop: '16px' }}
    >
      <defs>
        <linearGradient id="ppChartFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#b9d4f8" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#eaf2fd" stopOpacity="0.45" />
        </linearGradient>
      </defs>
      <path d={`${line} L${w},${h} L0,${h} Z`} fill="url(#ppChartFill)" />
      <path d={line} fill="none" stroke="#4f8ee8" strokeWidth="2" />
    </svg>
  );
}

function StatCard({ label, value, series }) {
  const { t } = useI18n();
  return (
    <Card>
      <Box padding="400" paddingBlockEnd="200">
        <InlineStack align="space-between" blockAlign="start">
          <Text variant="bodySm" as="p" fontWeight="medium">{label}</Text>
          <Text variant="bodySm" as="p" tone="subdued">{t('home.last30days')}</Text>
        </InlineStack>
        <Box paddingBlockStart="200">
          <Text variant="headingLg" as="p">{value}</Text>
        </Box>
      </Box>
      <Box padding="400" paddingBlockStart="0">
        {series ? <MiniAreaChart counts={series.map(d => d.count)} /> : (
          <div style={{ height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spinner accessibilityLabel={t('home.loadingChart')} size="small" />
          </div>
        )}
      </Box>
    </Card>
  );
}

// Getting-started checklist — every step's done-state is read from live
// data (plans on file, app-embed status, 30-day stats), so the card tracks
// the store's actual setup. It replaced the old tutorial-video carousel,
// which pointed at videos that don't exist.
function GettingStartedCard({
  plansCount, stats, embed, onCreatePlan, onEnableEmbed, onRetryEmbed
}) {
  const { t } = useI18n();
  const hasPlans = (plansCount ?? 0) > 0;
  const hasProtectedOrder = (stats?.ordersWithPlans ?? 0) > 0;
  const embedEnabled = embed?.enabled === true;

  const steps = [
    {
      title: t('home.step1.title'),
      description: t('home.step1.desc'),
      done: hasPlans,
      action: hasPlans ? null : { content: t('common.createPlan'), onAction: onCreatePlan }
    },
    {
      title: t('home.step2.title'),
      description: t('home.step2.desc'),
      done: embedEnabled,
      // No button while the status is still loading (embed === null);
      // an unknown status gets a re-check instead of a theme-editor hop.
      action: embed === null || embedEnabled ? null : (
        embed.enabled === false
          ? { content: t('home.step2.enable'), onAction: onEnableEmbed }
          : { content: t('home.step2.check'), onAction: onRetryEmbed }
      )
    },
    {
      title: t('home.step3.title'),
      description: t('home.step3.desc'),
      done: hasProtectedOrder,
      action: null
    }
  ];
  const doneCount = steps.filter(s => s.done).length;

  return (
    <Card>
      <Box padding="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingSm" as="h2">{t('home.gettingStarted')}</Text>
          {doneCount === steps.length
            ? <Badge tone="success">{t('home.allSet')}</Badge>
            : <Text variant="bodySm" as="span" tone="subdued">{t('home.completed', { done: doneCount, total: steps.length })}</Text>}
        </InlineStack>
        <Box paddingBlockStart="200">
          {steps.map((step, i) => (
            <div
              key={step.title}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 0',
                borderTop: i === 0 ? 'none' : '1px solid #f1f1f1'
              }}
            >
              {step.done
                ? <Icon source={CheckCircleIcon} tone="success" />
                : <div aria-hidden="true" style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #c9cccf', flexShrink: 0 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text
                  variant="bodyMd"
                  as="p"
                  fontWeight={step.done ? 'regular' : 'semibold'}
                  tone={step.done ? 'subdued' : undefined}
                >
                  {step.title}
                </Text>
                <Text variant="bodySm" as="p" tone="subdued">{step.description}</Text>
              </div>
              {step.action && (
                <Button size="slim" onClick={step.action.onAction}>{step.action.content}</Button>
              )}
            </div>
          ))}
        </Box>
      </Box>
    </Card>
  );
}

// App-embed status card — everything comes from GET /api/app-embed. enabled
// is true/false once the live theme was read; null means unknown (local demo
// mode, store not installed, or Shopify unreachable).
function AppEmbedCard({ status, embedded, shopDomain, onShowHint, onRetry }) {
  const { t } = useI18n();
  if (!status) {
    return (
      <Card>
        <Box padding="400">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingSm" as="h2">{t('home.appEmbed')}</Text>
            <Spinner accessibilityLabel={t('home.checkingEmbed')} size="small" />
          </InlineStack>
        </Box>
      </Card>
    );
  }

  if (status.enabled === true) {
    return (
      <Card>
        <Box padding="400">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingSm" as="h2">{t('home.appEmbed')}</Text>
            <Badge tone="success">{t('common.enabled')}</Badge>
          </InlineStack>
          <Box paddingBlockStart="200">
            <Text variant="bodyMd" as="p">
              {t('home.embedEnabledBody')}
              {status.theme ? ` ${t('home.embedTheme', { theme: status.theme })}` : ''}
            </Text>
          </Box>
        </Box>
      </Card>
    );
  }

  if (status.enabled === false) {
    return (
      <Card>
        <Box padding="400">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingSm" as="h2">{t('home.appEmbed')}</Text>
            <Badge tone="attention">{t('common.disabled')}</Badge>
          </InlineStack>
          <Box paddingBlockStart="200">
            <Text variant="bodyMd" as="p">
              {t('home.embedDisabledBody')}
            </Text>
          </Box>
          <Box paddingBlockStart="500">
            {embedded ? (
              <Button
                icon={ExternalIcon}
                onClick={() => window.open(
                  `https://${shopDomain}/admin/themes/current/editor?context=apps`,
                  '_blank', 'noopener'
                )}
              >
                {t('home.step2.enable')}
              </Button>
            ) : (
              <Button onClick={onShowHint}>{t('home.step2.enable')}</Button>
            )}
          </Box>
        </Box>
      </Card>
    );
  }

  return (
    <Card>
      <Box padding="400">
        <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingSm" as="h2">{t('home.appEmbed')}</Text>
          <Badge tone="info">{t('home.embedUnknown')}</Badge>
        </InlineStack>
        <Box paddingBlockStart="200">
          <Text variant="bodyMd" as="p">
            {status.reason === 'shop-not-installed' || status.reason === 'no-token'
              ? t('home.embedUnknownBody')
              : t('home.embedUnknownReason')}
          </Text>
        </Box>
        <Box paddingBlockStart="500">
          <Button onClick={onRetry}>{t('common.retry')}</Button>
        </Box>
      </Box>
    </Card>
  );
}

// Runs an async fetcher on mount (and whenever its identity changes) and
// stores the result; returns a stable reload function for manual retries and
// window-focus re-checks. Errors leave the previous value — the cards
// render their "unknown" state.
function useFetched(fetcher, setter) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let cancelled = false;
    fetcher().then(d => { if (!cancelled) setter(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, [fetcher, setter, tick]);
  return useCallback(() => setTick(t => t + 1), []);
}

export default function Home() {
  const { t } = useI18n();
  const [toast, setToast] = useState(null);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansCount, setPlansCount] = useState(null); // null = unknown yet
  const [stats, setStats] = useState(null); // null = still loading
  const [embed, setEmbed] = useState(null); // null = still loading
  const navigate = useNavigate();
  const { getPlans, getStats, getAppEmbedStatus } = useApi();
  const { shopDomain, getSessionToken } = useAuth();
  const embedded = Boolean(getSessionToken);

  // Loading gate for the stat cards only. Plans are never auto-seeded here:
  // an empty list is a real state, and seeding used to resurrect demo data
  // over whatever the merchant configured (demo plans are an explicit
  // "Load demo plans" action on the Plans page now). The count also drives
  // the "Create your first plan" checklist step.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const plans = await getPlans();
        if (!cancelled) setPlansCount(Array.isArray(plans) ? plans.length : 0);
      } catch {
        // The plans page surfaces API errors; the checklist treats this as
        // "no plans yet".
      } finally {
        if (!cancelled) setPlansLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getPlans]);

  useFetched(getStats, setStats);
  const loadEmbed = useFetched(getAppEmbedStatus, setEmbed);

  // Re-check the app-embed status whenever the window becomes active again.
  // The merchant's enable flow is "open the theme editor in another tab,
  // toggle the block on, switch back" — the switch back must re-read the
  // live theme, not wait for the next full page load. visibilitychange
  // covers tab switches; focus covers the editor living in a separate
  // browser window, where tab visibility never changes. ?refresh=1 skips
  // the backend's result cache so the just-made edit shows up immediately;
  // a 5s floor keeps rapid window switching from spamming Shopify.
  const lastEmbedCheckRef = useRef(0);
  useEffect(() => {
    let alive = true;
    const recheck = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastEmbedCheckRef.current < 5000) return;
      lastEmbedCheckRef.current = Date.now();
      getAppEmbedStatus({ refresh: true })
        .then(d => { if (alive) setEmbed(d); })
        .catch(() => {});
    };
    document.addEventListener('visibilitychange', recheck);
    window.addEventListener('focus', recheck);
    return () => {
      alive = false;
      document.removeEventListener('visibilitychange', recheck);
      window.removeEventListener('focus', recheck);
    };
  }, [getAppEmbedStatus]);

  return (
    <Page
      title={t('home.title')}
      primaryAction={{ content: t('common.createPlan'), icon: PlusIcon, onAction: () => navigate('/plans/new') }}
    >
      <div style={{ maxWidth: '1040px', margin: '0 auto' }}>
        <Layout>
          <Layout.Section>
            {plansLoading ? (
              <div style={{ padding: '64px', textAlign: 'center' }}>
                <Spinner accessibilityLabel={t('home.loadingOverview')} size="large" />
              </div>
            ) : (
              <Layout>
                <Layout.Section variant="oneThird">
                  <StatCard
                    label={t('home.statOrders')}
                    value={stats ? String(stats.ordersWithPlans) : '…'}
                    series={stats ? stats.series : null}
                  />
                </Layout.Section>
                <Layout.Section variant="oneThird">
                  <StatCard
                    label={t('home.statRevenue')}
                    value={stats ? formatMoney(stats.planRevenue) : '…'}
                    series={stats ? stats.series : null}
                  />
                </Layout.Section>
                <Layout.Section variant="oneThird">
                  <AppEmbedCard
                    status={embed}
                    embedded={embedded}
                    shopDomain={shopDomain}
                    onShowHint={() => setToast({ content: t('home.enableEmbedHint') })}
                    onRetry={loadEmbed}
                  />
                </Layout.Section>
              </Layout>
            )}
          </Layout.Section>

          <Layout.Section>
            <GettingStartedCard
              plansCount={plansCount}
              stats={stats}
              embed={embed}
              onCreatePlan={() => navigate('/plans/new')}
              onEnableEmbed={() => {
                if (embedded) {
                  window.open(
                    `https://${shopDomain}/admin/themes/current/editor?context=apps`,
                    '_blank', 'noopener'
                  );
                } else {
                  setToast({ content: t('home.enableEmbedHint') });
                }
              }}
              onRetryEmbed={loadEmbed}
            />
          </Layout.Section>

          <Layout.Section>
            <Text variant="bodyMd" as="p" alignment="center">
              {t('home.learnMorePrefix')}
              <Link url="https://flexcover.gitbook.io/flexcover-protection-plans" external>{t('home.learnMoreLink')}</Link>
            </Text>
          </Layout.Section>
        </Layout>
      </div>

      {toast && <Toast content={toast.content} onDismiss={() => setToast(null)} duration={6000} />}
    </Page>
  );
}
